// Upload step - allows user to upload one or more photos.
//

import { ArrowRight, Check, Plus, Upload, X } from "lucide-react";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import { Button } from "@/ui/button";

import type { PhotoItem } from "../index";
import { Requirements } from "../ui/requirements";
import { PHOTO_SIZES, type PhotoSize } from "../utils/dimensions";

// Check if a file is HEIC/HEIF format.
//
function isHeicFile(file: File): boolean {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type === "image/heic" ||
    type === "image/heif" ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

// Convert HEIC file to JPEG (dynamically imports heic2any to avoid SSR issues).
//
async function convertHeicToJpeg(file: File): Promise<File> {
  const heic2any = (await import("heic2any")).default;

  const blob = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.92,
  });

  // heic2any can return a single blob or array of blobs.
  //
  const resultBlob = Array.isArray(blob) ? blob[0] : blob;
  if (!resultBlob) {
    throw new Error("Failed to convert HEIC file");
  }

  const newName = file.name
    .replace(/\.heic$/i, ".jpg")
    .replace(/\.heif$/i, ".jpg");

  return new File([resultBlob], newName, { type: "image/jpeg" });
}

// Process files, converting HEIC to JPEG as needed.
//
async function processFiles(files: File[]): Promise<File[]> {
  const processed: File[] = [];

  for (const file of files) {
    if (isHeicFile(file)) {
      try {
        const converted = await convertHeicToJpeg(file);
        processed.push(converted);
      } catch (error) {
        console.error("Failed to convert HEIC file:", file.name, error);
        // Skip files that fail to convert.
      }
    } else {
      processed.push(file);
    }
  }

  return processed;
}

interface UploadStepProps {
  photos: PhotoItem[];
  photoSize: PhotoSize;
  removeBackground: boolean;
  onUpload: (files: File[]) => void;
  onPhotoSizeChange: (photoSize: PhotoSize) => void;
  onBackgroundRemovalChange: (removeBackground: boolean) => void;
  onContinue: () => void;
  onRemovePhoto: (photoId: string) => void;
}

// Upload step component.
//
export function UploadStep({
  photos,
  photoSize,
  removeBackground,
  onUpload,
  onPhotoSizeChange,
  onBackgroundRemovalChange,
  onContinue,
  onRemovePhoto,
}: UploadStepProps) {
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());
  const [isConverting, setIsConverting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoSizes = Object.entries(PHOTO_SIZES) as [
    PhotoSize,
    (typeof PHOTO_SIZES)[PhotoSize],
  ][];
  const backgroundTip = removeBackground
    ? "Use an evenly lit background for the cleanest automatic removal"
    : photoSize === "33x48"
      ? "Stand against a plain white background"
      : "Stand against a plain white or off-white background";

  // Generate thumbnails for uploaded photos.
  //
  useEffect(() => {
    const newThumbnails = new Map<string, string>();
    photos.forEach((photo) => {
      const existingThumbnail = thumbnails.get(photo.id);

      if (existingThumbnail) {
        newThumbnails.set(photo.id, existingThumbnail);
      } else {
        const url = URL.createObjectURL(photo.originalImage);
        newThumbnails.set(photo.id, url);
      }
    });

    // Clean up old URLs.
    //
    thumbnails.forEach((url, id) => {
      if (!photos.find((p) => p.id === id)) {
        URL.revokeObjectURL(url);
      }
    });

    setThumbnails(newThumbnails);

    return () => {
      // Clean up all URLs on unmount.
      //
      newThumbnails.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- thumbnails causes infinite loop
  }, [photos]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      // Check if any files need HEIC conversion.
      //
      const hasHeic = acceptedFiles.some(isHeicFile);

      if (hasHeic) {
        setIsConverting(true);
        try {
          const processed = await processFiles(acceptedFiles);
          if (processed.length > 0) {
            onUpload(processed);
          }
        } finally {
          setIsConverting(false);
        }
      } else {
        onUpload(acceptedFiles);
      }
    },
    [onUpload],
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept } =
    useDropzone({
      onDrop: (acceptedFiles) => {
        void onDrop(acceptedFiles);
      },
      accept: {
        "image/*": [".jpeg", ".jpg", ".png", ".webp", ".heic", ".heif"],
      },
      multiple: true,
      disabled: isConverting,
    });

  const handleAddMore = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if any files need HEIC conversion.
    //
    const hasHeic = files.some(isHeicFile);

    if (hasHeic) {
      setIsConverting(true);
      try {
        const processed = await processFiles(files);
        if (processed.length > 0) {
          onUpload(processed);
        }
      } finally {
        setIsConverting(false);
      }
    } else {
      onUpload(files);
    }

    // Reset input so same files can be selected again.
    //
    e.target.value = "";
  };

  const baseClasses =
    "flex min-h-64 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-colors";

  const stateClasses = isDragAccept
    ? "border-green-500 bg-green-50 dark:bg-green-950"
    : isDragActive
      ? "border-primary bg-accent"
      : "border-border hover:border-primary/50 hover:bg-accent";

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold">Upload Your Photos</h3>
        <p className="text-muted-foreground mt-1">
          Choose one or more photos with a clear view of faces.
        </p>
      </div>

      {/* Photo size options */}
      <fieldset className="space-y-3">
        <legend className="font-medium">Photo size</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {photoSizes.map(([sizeKey, sizeConfig]) => {
            const isSelected = photoSize === sizeKey;

            return (
              <button
                key={sizeKey}
                aria-pressed={isSelected}
                type="button"
                className={`relative flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all ${
                  isSelected
                    ? "border-primary bg-accent"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => onPhotoSizeChange(sizeKey)}
              >
                <div
                  className="border-primary/60 h-16 flex-none rounded border-2 bg-white"
                  style={{
                    aspectRatio: `${sizeConfig.pixels.width} / ${sizeConfig.pixels.height}`,
                  }}
                />
                <div>
                  <div className="font-semibold">{sizeConfig.label}</div>
                  <div className="text-muted-foreground text-sm">
                    {sizeConfig.alternateLabel}
                  </div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    {sizeConfig.application}
                  </div>
                </div>
                {isSelected && (
                  <div className="bg-primary text-primary-foreground absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full">
                    <Check className="h-3 w-3" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Background options */}
      <fieldset className="space-y-3">
        <legend className="font-medium">Background</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            {
              remove: true,
              label: "Remove automatically",
              description: "Replace the uploaded background with white.",
            },
            {
              remove: false,
              label: "Keep original",
              description: "Preserve the background already in the photo.",
            },
          ].map((option) => {
            const isSelected = removeBackground === option.remove;

            return (
              <button
                key={option.label}
                aria-pressed={isSelected}
                type="button"
                className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                  isSelected
                    ? "border-primary bg-accent"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => onBackgroundRemovalChange(option.remove)}
              >
                <div className="pr-7 font-semibold">{option.label}</div>
                <div className="text-muted-foreground mt-1 text-sm">
                  {option.description}
                </div>
                {isSelected && (
                  <div className="bg-primary text-primary-foreground absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full">
                    <Check className="h-3 w-3" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Hidden file input for "Add More" button */}
      <input
        ref={fileInputRef}
        multiple
        accept="image/*,.heic,.heif"
        className="hidden"
        type="file"
        onChange={(e) => {
          void handleFileInputChange(e);
        }}
      />

      {/* Converting indicator */}
      {isConverting && (
        <div className="bg-accent flex items-center justify-center gap-3 rounded-xl p-4">
          <div className="border-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
          <span className="text-muted-foreground text-sm">
            Converting HEIC files...
          </span>
        </div>
      )}

      {/* Show dropzone or thumbnail grid */}
      {photos.length === 0 ? (
        <div {...getRootProps()} className={`${baseClasses} ${stateClasses}`}>
          <input {...getInputProps()} />

          <Upload className="text-muted-foreground mb-4 h-12 w-12" />

          {isDragActive ? (
            <p className="text-primary text-lg">Drop the images here...</p>
          ) : (
            <>
              <p className="text-lg">Drag and drop images here</p>
              <p className="text-muted-foreground mt-2 text-sm">
                or click to select files
              </p>
            </>
          )}

          <p className="text-muted-foreground mt-4 text-xs">
            Supports JPEG, PNG, WebP, and HEIC (iPhone). Select multiple photos
            at once.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Thumbnail grid */}
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="group bg-accent relative aspect-square overflow-hidden rounded-lg border"
              >
                {thumbnails.has(photo.id) && (
                  <img
                    alt="Uploaded photo"
                    className="h-full w-full object-cover"
                    src={thumbnails.get(photo.id)}
                  />
                )}
                {/* Remove button */}
                <button
                  className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-600"
                  type="button"
                  onClick={() => onRemovePhoto(photo.id)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Add more button */}
            <button
              className="text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-accent flex aspect-square items-center justify-center rounded-lg border-2 border-dashed transition-colors"
              type="button"
              onClick={handleAddMore}
            >
              <Plus className="h-8 w-8" />
            </button>
          </div>

          <p className="text-muted-foreground text-center text-sm">
            {photos.length} photo{photos.length !== 1 ? "s" : ""} selected
          </p>
        </div>
      )}

      <div className="bg-accent rounded-xl border p-4">
        <h4 className="font-medium">Tips for best results:</h4>
        <ul className="text-muted-foreground mt-2 list-inside list-disc space-y-1 text-sm">
          <li>Use a recent photo taken within the last 6 months</li>
          <li>Face the camera directly with a neutral expression</li>
          <li>Remove glasses and hats (religious exceptions allowed)</li>
          <li>Use good lighting with no shadows on your face</li>
          <li>{backgroundTip}</li>
        </ul>
      </div>

      <Requirements photoSize={photoSize} />

      {/* Continue button when photos are uploaded */}
      {photos.length > 0 && (
        <div className="flex justify-end">
          <Button onClick={onContinue}>
            Continue
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
